import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/helper/api";
import dayjs from "dayjs";
import GeneralListItem from "@/components/general/generaListItem";
import { Grid, Tooltip } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddIcon from '@mui/icons-material/Add';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { useTranslation } from "react-i18next";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import TimeSlotItem from "@/components/appointmentRequest/timeSlotItem";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import Modal from "@/components/modal";
import GenericDateTime from "@/components/dateTime/genericDateTime";
import GenericNotification from "@/components/notification/genericNotification";
import styles from "@/components/appointmentRequest/timeSlotsEdit.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import DefaultInput from "@/components/forms/inputs/defaultInput";
import { useConfirmationModal } from "@/context/confirmationModalContext";

const dayHasOnlyDisabledSlots = (slots: any[]) =>
  slots.length > 0 && slots.every((slot) => slot.disabled === true);

const TimeSlotsEdit: FC = () => {
  const { t } = useTranslation();
  usePageHeading(t("edit_available_timeslots"));
  const [appointmentRequest, setAppointmentRequest] = useState<any>([]);
  const [selectedDay, setSelectedDay] = useState<string>("");
  const [timeslotsForDay, setTimeslotsForDay] = useState<any>([]);

  const [openAddTimeSlotModal, setOpenAddTimeSlotModal] =
    useState<boolean>(false);
  const { handleError, errorModal } = useErrorModal();

  const [newTimeSlotStartDate, setNewTimeSlotStartDate] = useState<any>(
    dayjs(new Date()),
  );
  const [newtimeSlotEndDate, setNewTimeSlotEndDate] = useState<any>(
    dayjs(new Date()),
  );
  const [slotduration, setSlotDuration] = useState<number>(1);
  const [numberOfNewSlots, setNumberOfNewSlots] = useState<number>(1);
  const [allSlotData, setAllSlotData] = useState<any>();
  const [successForNotification, setSuccessForNotification] =
    useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();
  const [loadingSlots, setLoadingSlots] = useState<Set<string>>(new Set());
  const [isDeletingDay, setIsDeletingDay] = useState(false);

  const { appointmentrequestId } = useParams() as {
    appointmentrequestId: string;
  };
  const navigate = useNavigate();
  const { showConfirmationModalAsync } = useConfirmationModal();

  const resetStateOnCloseNotification = () => {
    setSuccessForNotification(false);
  };

  useEffect(() => {
    getAppointmentSlots();
  }, []);

  const getAppointmentSlots = () => {
    let itemCollection: any = [];
    const additionalUrlParameters: any = {};

    additionalUrlParameters["appointment_request"] = appointmentrequestId;
    additionalUrlParameters["depth"] = "0";
    additionalUrlParameters["limit"] = "50";

    api.genericApiRequest({
      entity: "appointmentSlot",
      method: "get",
      getAllPages: true,
      parametersToRender: additionalUrlParameters,
      successHandler: (res: any) => {
        itemCollection = res.data.results;
        const allSlotData: any = {};
        for (const key in itemCollection) {
          const item = itemCollection[key];
          allSlotData[item.id] = item;
        }
        setAllSlotData(allSlotData);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  useEffect(() => {
    const allDates: any = {};
    for (const key in allSlotData) {
      const item = allSlotData[key];
      const startDate = dayjs(item.start_date).format("DD.MM.YYYY");
      if (!allDates[startDate]) {
        allDates[startDate] = [];
      }

      allDates[startDate].push(item);
    }

    setAppointmentRequest(allDates);
  }, [allSlotData]);

  useEffect(() => {
    updateSlotItemsForSelectedDay();
  }, [selectedDay, appointmentRequest]);

  useEffect(() => {
    if (openAddTimeSlotModal) {
      const calculatedEndtime = new Date(
        newTimeSlotStartDate.$d.getTime() +
          slotduration * 60 * 1000 * numberOfNewSlots,
      );
      setNewTimeSlotEndDate(dayjs(calculatedEndtime));
    }
  }, [
    slotduration,
    numberOfNewSlots,
    newTimeSlotStartDate,
    openAddTimeSlotModal,
  ]);

  const updateSlotItemsForSelectedDay = () => {
    let timeSlotStartDateForNewSlots = undefined;
    let slotDurationForNewSlots = undefined;

    if (
      selectedDay !== "" &&
      selectedDay in appointmentRequest &&
      appointmentRequest[selectedDay] !== undefined
    ) {
      const sortedSlotsForSelectedDay = appointmentRequest[selectedDay].sort(
        (a: any, b: any) => a.start_date.localeCompare(b.start_date),
      );

      const slotsCount = sortedSlotsForSelectedDay.length;
      let counter = 0;

      for (const key in sortedSlotsForSelectedDay) {
        counter++;

        if (counter === slotsCount) {
          slotDurationForNewSlots = sortedSlotsForSelectedDay[key].duration;
          timeSlotStartDateForNewSlots = dayjs(
            sortedSlotsForSelectedDay[key].start_date,
          ).add(slotDurationForNewSlots, "minutes");
        }
      }

      setNewTimeSlotStartDate(timeSlotStartDateForNewSlots);
      setSlotDuration(slotDurationForNewSlots);
      setTimeslotsForDay(sortedSlotsForSelectedDay);
    }
  };

  const handleSelectDay = (day: any) => {
    setSelectedDay(day);
  };

  const handleSelectNewTimeSlotDay = (day: any) => {
    setNewTimeSlotStartDate(dayjs(day));
    setNewTimeSlotEndDate(dayjs(day));
  };

  const handleSlotDurationChange = (event: any) => {
    setSlotDuration(parseInt(event.target.value));
  };
  const handleNumberOfNewSlots = (event: any) => {
    setNumberOfNewSlots(parseInt(event.target.value));
  };

  const handleNavigateBack = () => {
    navigate("/appointmentrequest");
  };

  const handleSubmitNewTimeslots = async () => {
    const newEndDate = new Date(
      newTimeSlotStartDate.$d.getTime() + slotduration * 60000,
    );
    const result = {
      appointment_request: appointmentrequestId,
      duration: slotduration,
      start_date: newTimeSlotStartDate.$d.toISOString(),
      end_date: newEndDate.toISOString(),
    };

    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    try {
      const promises = [];
      let startTime = newTimeSlotStartDate.$d;
      for (let i = 0; i < numberOfNewSlots; i++) {
        const endTime = new Date(startTime);
        endTime.setMinutes(endTime.getMinutes() + result.duration);
        const newResult = {
          ...result,
          start_date: startTime,
          end_date: endTime,
        };
        promises.push(
          new Promise((resolve) => {
            api.genericApiRequest({
              entity: "appointmentSlot",
              method: "post",
              submitData: newResult,
              config: config,

              successHandler: (res: any) => {
                resolve(res.data);
              },
              failHandler: (error: any) => {
                let tempError = error;
                if (error.detail) {
                  tempError = {
                    "": [t("error_occurred")],
                  };
                }
                resolve({ error: tempError, status: "false" });
              },
            });
          }),
        );
        startTime = endTime;
      }
      await Promise.all(promises).then((promiseResults: any) => {
        const failedPosts = [];
        const newSlots: any = {};

        for (const promiseResultItem of promiseResults) {
          if (
            promiseResultItem.status &&
            promiseResultItem.status === "false"
          ) {
            failedPosts.push(promiseResultItem.error);
          } else {
            newSlots[promiseResultItem.id] = promiseResultItem;
          }
        }

        setAllSlotData(Object.assign({}, allSlotData, newSlots));
        if (failedPosts.length !== 0) {
          const combinedErrors = failedPosts.reduce((acc, obj) => {
            const key = Object.keys(obj)[0];
            acc[key] = obj[key];
            return acc;
          }, {});

          handleError(combinedErrors);
        } else {
          setOpenAddTimeSlotModal(false);

          setNotificationVariant("success");
          setNotificationMessage(
            promiseResults.length > 1
              ? t("slot_successful_added_multi")
              : t("slot_successful_added"),
          );
          setSuccessForNotification(true);
        }
      });
    } catch {
      //empty comment for linter
    }
  };

  const handleToggleDisabled = (slot: any, disabled: boolean) => {
    const config = { headers: { "Content-Type": "application/json" } };
    const result = {
      start_date: slot.start_date,
      end_date: slot.end_date,
      duration: slot.duration,
      appointment_request: slot.appointment_request.id,
      disabled: disabled ? false : true,
    };

    api.genericApiRequest({
      method: "put",
      entity: "appointmentSlot",
      entityId: slot.id,
      submitData: result,
      config,
      successHandler: (res: any) => {
        allSlotData[slot.id].disabled = res.data.disabled;
        setAllSlotData({ ...allSlotData });
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  const bulkUpdateSlots = (slots: any[], fields: object) =>
    new Promise<any[] | null>((resolve) => {
      api.genericApiRequest({
        entity: "appointmentSlot",
        method: "put",
        submitData: slots.map((slot: any) => ({ id: slot.id, ...fields })),
        config: { headers: { "Content-Type": "application/json" } },
        successHandler: (res: any) => resolve(res.data),
        failHandler: (error: any) => {
          handleError(error);
          resolve(null);
        },
      });
    });

  const handleAllSlots = async (disableAll: boolean) => {
    const slotsToUpdate = disableAll
      ? timeslotsForDay.filter((slot: any) => slot.appointment === null)
      : timeslotsForDay;

    if (slotsToUpdate.length === 0) {
      return;
    }

    const idsToUpdate = slotsToUpdate.map((slot: any) => slot.id);
    setLoadingSlots((prev) => {
      const next = new Set(prev);
      for (const id of idsToUpdate) {
        next.add(id);
      }
      return next;
    });

    const updatedSlots = await bulkUpdateSlots(slotsToUpdate, {
      disabled: disableAll,
    });

    if (updatedSlots) {
      const updatedSlotData = { ...allSlotData };
      for (const updatedSlot of updatedSlots) {
        if (updatedSlotData[updatedSlot.id]) {
          updatedSlotData[updatedSlot.id] = {
            ...updatedSlotData[updatedSlot.id],
            disabled: updatedSlot.disabled,
          };
        }
      }
      setAllSlotData(updatedSlotData);
    }

    setLoadingSlots((prev) => {
      const next = new Set(prev);
      for (const id of idsToUpdate) {
        next.delete(id);
      }
      return next;
    });

    updateSlotItemsForSelectedDay();
  };

  const handleDeleteEntireDay = async (day: string) => {
    const slots = appointmentRequest[day];
    if (!slots || !dayHasOnlyDisabledSlots(slots)) {
      return;
    }

    const confirmed = await showConfirmationModalAsync(
      t("confirm_delete_entire_day", { day, count: slots.length }),
      {
        title: t("delete_entire_day"),
        confirmButtonText: t("delete_entry"),
      },
    );

    if (!confirmed) {
      return;
    }

    setIsDeletingDay(true);

    const updatedSlots = await bulkUpdateSlots(slots, { deleted: true });

    if (updatedSlots) {
      const updatedSlotData = { ...allSlotData };
      for (const slot of slots) {
        delete updatedSlotData[slot.id];
      }
      setAllSlotData(updatedSlotData);

      if (selectedDay === day) {
        setSelectedDay("");
      }

      setNotificationVariant("success");
      setNotificationMessage(t("day_deleted_successfully"));
      setSuccessForNotification(true);
    }

    setIsDeletingDay(false);
  };

  const canDeleteSelectedDay =
    selectedDay !== "" &&
    selectedDay in appointmentRequest &&
    dayHasOnlyDisabledSlots(appointmentRequest[selectedDay]);

  const deleteSelectedDayTooltip = canDeleteSelectedDay
    ? undefined
    : t("cannot_delete_entire_day_open_slots");

  const selectedDaySlots =
    selectedDay !== "" && selectedDay in appointmentRequest
      ? appointmentRequest[selectedDay]
      : [];

  const canActivateAllSlots = selectedDaySlots.some(
    (slot: any) => slot.disabled === true,
  );

  const canDeactivateAllSlots = selectedDaySlots.some(
    (slot: any) => slot.disabled === false && slot.appointment === null,
  );

  const renderedDaysButtons = [];
  const renderedSlotItemsForSelectedDay = [];

  const sortedDays = Object.keys(appointmentRequest).sort(
    (a: any, b: any) =>
      dayjs(a, "DD.MM.YYYY").toDate().getTime() -
      dayjs(b, "DD.MM.YYYY").toDate().getTime(),
  );

  for (const key in sortedDays) {
    const day = sortedDays[key];
    const slotsForDay = appointmentRequest[day] ?? [];
    const showDeleteDayIcon = dayHasOnlyDisabledSlots(slotsForDay);

    renderedDaysButtons.push(
      <Grid
        item
        key={day}
        xs={6}
        md={4}
        lg={3}
        className={styles.dayGridItem}
      >
        <GeneralListItem
          allowHover={true}
          hideButton={true}
          margin="0"
          padding="0.75rem"
          onClickElement="wrapper"
          onClick={() => handleSelectDay(day)}
          wrapperStyle={{ width: "100%" }}
          wrapperClassName={`
            ${styles.generalListItemWrapper}
            ${
              selectedDay === day
                ? styles.generalListItemWrapper__selectedDay
                : styles.generalListItemWrapper__noSelectedDay
            }
          `}
          contentClassName={styles.dayListItemContent}
        >
          <span className={styles.dayLabel}>{day}</span>
          {showDeleteDayIcon && (
            <Tooltip arrow placement="top" title={t("delete_entire_day")}>
              <span
                className={`${styles.deleteDayIconWrapper} ${
                  isDeletingDay ? styles.deleteDayIconWrapperDisabled : ""
                }`}
                onClick={(event) => {
                  event.stopPropagation();
                  if (!isDeletingDay) {
                    handleDeleteEntireDay(day);
                  }
                }}
              >
                <span className={styles.deleteDayIconCircle}>
                  <DeleteForeverIcon className={styles.deleteDayIcon} />
                </span>
              </span>
            </Tooltip>
          )}
        </GeneralListItem>
      </Grid>,
    );
  }

  if (selectedDay !== "" && appointmentRequest[selectedDay] !== undefined) {
    const slots = appointmentRequest[selectedDay].sort((a: any, b: any) =>
      a.start_date.localeCompare(b.start_date),
    );

    for (const key in slots) {
      const slot = slots[key];
      const slotId = slot.id;

      renderedSlotItemsForSelectedDay.push(
        <TimeSlotItem
          key={slotId}
          slotData={slot}
          handleToggleDisabled={handleToggleDisabled}
          loading={loadingSlots.has(slotId)}
        />,
      );
    }
  }

  const handleCloseNewTimeSlotModal = () => {
    setOpenAddTimeSlotModal(false);
    setNumberOfNewSlots(1);
  };

  return (
    <>
      <Tooltip placement="right" title={t("back")}>
        <GenericButton
          variant="icon"
          color="iconBase"
          onClick={handleNavigateBack}
          className={styles.navigateBackButton}
        >
          <ArrowBackIcon />
        </GenericButton>
      </Tooltip>
      <Grid container columnSpacing={"1rem"} className={styles.container}>
        <Grid item xs={12}>
          <div className={styles.slotButtonsContainer}>
            <h2>{t("select_date")}</h2>
          </div>
        </Grid>
        <Grid
          item
          container
          xs={12}
          columnSpacing={"1rem"}
          rowSpacing={"1rem"}
          mb={"1.5rem"}
        >
          {renderedDaysButtons}
        </Grid>
        {selectedDay !== "" && (
          <Grid
            item
            container
            xs={12}
            columnSpacing={"1rem"}
            rowSpacing={"1rem"}
          >
            <Grid item xs={12}>
              <div className={styles.slotButtonsContainer}>
                <h2>{t("select_timeslot")}</h2>
                {selectedDay !== "" && (
                  <>
                    <GenericButton
                      variant="outlined"
                      color="tertiary"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setOpenAddTimeSlotModal(true);
                      }}
                      className={styles.buttonStyle}
                    >
                      {t("add_slot")}
                    </GenericButton>
                    <GenericButton
                      variant="outlined"
                      color="secondary"
                      startIcon={<EventAvailableIcon />}
                      onClick={() => {
                        handleAllSlots(false);
                      }}
                      className={styles.buttonStyle}
                      disabled={!canActivateAllSlots}
                    >
                      {t("activate_all_slots")}
                    </GenericButton>
                    <GenericButton
                      variant="outlined"
                      color="error"
                      startIcon={<EventBusyIcon />}
                      onClick={() => handleAllSlots(true)}
                      className={`${styles.buttonStyle} ${styles.deactivateAllSlotsButton}`}
                      disabled={!canDeactivateAllSlots}
                    >
                      {t("deactivate_all_slots")}
                    </GenericButton>
                    <Tooltip arrow placement="top" title={deleteSelectedDayTooltip}>
                      <span className={styles.buttonTooltipWrapper}>
                        <GenericButton
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteForeverIcon />}
                          onClick={() => handleDeleteEntireDay(selectedDay)}
                          className={styles.buttonStyle}
                          disabled={!canDeleteSelectedDay || isDeletingDay}
                        >
                          {t("delete_entire_day")}
                        </GenericButton>
                      </span>
                    </Tooltip>
                  </>
                )}
              </div>
            </Grid>
            {renderedSlotItemsForSelectedDay}
          </Grid>
        )}
      </Grid>

      {openAddTimeSlotModal && (
        <Modal
          open={openAddTimeSlotModal}
          onClose={handleCloseNewTimeSlotModal}
          contentStyle={{ paddingTop: "1rem !important" }}
          onSubmit={handleSubmitNewTimeslots}
          title={t("add_slot")}
        >
          <Grid container rowSpacing={"1rem"} columnSpacing={"1rem"}>
            <Grid item xs={6}>
              <GenericDateTime
                value={newTimeSlotStartDate}
                onChange={(date: any) => handleSelectNewTimeSlotDay(date)}
                className={styles.dateTimePicker}
                clearable={false}
                textFieldProps={{ label: t("start_time") }}
              />
            </Grid>
            {slotduration !== undefined && (
              <Grid
                container
                item
                xs={12}
                columnSpacing={"1rem"}
                rowSpacing={"1rem"}
              >
                <Grid item xs={6}>
                  <DefaultInput
                    label={t("duration")}
                    type="number"
                    value={
                      Number.isNaN(slotduration) ? "" : String(slotduration)
                    }
                    onChange={handleSlotDurationChange}
                    width="100%"
                  />
                </Grid>

                <Grid item xs={6}>
                  <DefaultInput
                    label={t("number_slots")}
                    type="number"
                    value={
                      Number.isNaN(numberOfNewSlots)
                        ? ""
                        : String(numberOfNewSlots)
                    }
                    onChange={handleNumberOfNewSlots}
                    width="100%"
                  />
                </Grid>
                <Grid item xs={6}>
                  <GenericDateTime
                    value={newtimeSlotEndDate}
                    onChange={() => undefined}
                    className={styles.dateTimePicker}
                    disabled
                    clearable={false}
                    textFieldProps={{ label: t("end_time") }}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        </Modal>
      )}

      <GenericNotification
        message={successForNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
      {errorModal}
    </>
  );
};

export default TimeSlotsEdit;
